import React, { useState, useEffect } from "react";
import { 
  FileText, 
  Download, 
  Search, 
  Filter, 
  Calendar, 
  User,
  PlusCircle,
  FileCheck,
  Activity
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase/config";
import { ref, onValue, push } from "firebase/database";
import PatientSidebar from "../../components/PatientSidebar";
import "./MedicalRecords.css";

const MedicalRecords = () => {
  const { currentUser } = useAuth();
  const { showAlert } = useToast();
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    const recordsRef = ref(db, `records/${currentUser.uid}`);
    const unsubscribe = onValue(recordsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data).map(([id, val]) => ({
          id,
          ...val
        }));
        setRecords(list.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } else {
        setRecords([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleUploadRecord = async () => {
    try {
      const recordsRef = ref(db, `records/${currentUser.uid}`);
      await push(recordsRef, {
        title: "Clinical Analysis Report",
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
        type: "Lab Report",
        doctor: "Dr. Sarah Wilson",
        size: "1.2 MB",
        format: "PDF",
        status: "Synced"
      });
      showAlert("Record uploaded successfully!", "success");
    } catch (error) {
      showAlert("Error: " + error.message, "error");
    }
  };

  return (
    <div className="dashboard-shell">
      <PatientSidebar />

      <main className="dashboard-content">
        <header className="records-header">
          <div className="header-text">
            <h1>Medical Records</h1>
            <p>Access and manage your clinical reports and digital health data</p>
          </div>
          <button className="premium-btn primary" onClick={handleUploadRecord}>
            <PlusCircle size={18} />
            Upload New Record
          </button>
        </header>

        <div className="scrollable-content records-grid">
          <div className="records-view-controls">
            <div className="search-box glass-card">
              <Search size={18} className="icon-dim" />
              <input type="text" placeholder="Search reports, doctors, or keywords..." />
            </div>
            
            <div className="filter-group">
              <button className="filter-btn glass-card">
                <Filter size={16} />
                <span>Filter</span>
              </button>
              <button className="filter-btn glass-card">
                <Calendar size={16} />
                <span>Date Range</span>
              </button>
            </div>
          </div>

          <section className="records-list-section">
            <div className="records-list">
              {records.map((record) => (
                <div key={record.id} className="record-card-large glass-card">
                  <div className="record-icon-box">
                    <FileCheck size={28} className="icon-blue" />
                  </div>
                  
                  <div className="record-main-info">
                    <div className="record-header-row">
                      <div className="record-title-meta">
                        <h3>{record.title}</h3>
                        <div className="record-type-badge">{record.type}</div>
                      </div>
                      <span className={`sync-status ${record.status.toLowerCase().replace(" ", "-")}`}>
                        {record.status}
                      </span>
                    </div>
                    
                    <div className="record-details-grid">
                      <div className="detail-item">
                        <Calendar size={14} />
                        <span>{record.date}</span>
                      </div>
                      <div className="detail-item">
                        <User size={14} />
                        <span>{record.doctor}</span>
                      </div>
                      <div className="detail-item">
                        <Activity size={14} />
                        <span>{record.format} • {record.size}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="record-actions">
                    <button className="record-action-btn view">View Online</button>
                    <button className="record-action-btn download">
                      <Download size={18} />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="clinical-summary-mockup">
            <div className="summary-banner glass-card">
              <div className="banner-content">
                <div className="banner-text">
                  <h3>Clinical Summary v4.2</h3>
                  <p>Your comprehensive health profile is ready for review. Generated on Dec 01, 2025.</p>
                </div>
                <button className="premium-btn secondary">View Summary</button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default MedicalRecords;
